import { type ReactNode } from 'react';
import { Sidebar } from './Sidebar';
import { MobileTabBar } from './MobileTabBar';
import { ThemeSync } from './ThemeSync';
import { ThemeApplier } from './ThemeApplier';
import { ThemePreviewListener } from './ThemePreviewListener';
import { ConnectionRequiredSheet } from './ConnectionRequiredSheet';
import { AppTopbar } from './AppTopbar';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ReleaseBanner } from './ReleaseBanner';
import { useAnalysisStore, useSidebarStore } from '../lib/stores';
import { Icon } from '../lib/icons';
import { APP_VERSION_LABEL } from '../lib/version';
import { useActivityCenterFlag } from '../lib/featureFlags';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const tasks = useAnalysisStore((s) => s.tasks);
  const togglePanel = useAnalysisStore((s) => s.togglePanel);
  const { mobileOpen, setMobileOpen } = useSidebarStore();
  // Unified Activity Center + slim topbar dark-launch (default ON).
  const activityCenter = useActivityCenterFlag();

  // Theme application (palette/mode/contrast + custom) lives in <ThemeApplier>;
  // live cross-window edit preview is received by <ThemePreviewListener> below.
  const runningCount = tasks.filter((t) => t.status === 'running').length;
  const hasAny = tasks.length > 0;

  return (
    <div className="app-layout">
      <ThemeSync />
      <ThemeApplier />
      <ThemePreviewListener />
      {/* Global "connect your account first" prompt (raised by the API layer on 412). */}
      <ConnectionRequiredSheet />
      <Sidebar />

      {/* Mobile Overlay — always in DOM for CSS transitions */}
      <div
        className={`mobile-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* The `activity-shell` modifier drives the flag-on sticky-offset CSS
          (topbar height) without touching every page. */}
      <main className={`app-main${activityCenter ? ' activity-shell' : ''}`}>
        {activityCenter ? (
          /* Flag ON — the slim app topbar unifies the legacy mobile-header and
             hosts the single Activity Center indicator (no floating ⚡ button). */
          <AppTopbar />
        ) : (
          <>
            {/* Mobile Header — branding only; navigation lives in the bottom tab bar. */}
            <div className="mobile-header">
              <img src="/logo.svg" alt="" width={22} height={22} style={{ flexShrink: 0, borderRadius: '50%' }} />
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, minWidth: 0 }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--text-base)' }}>Personal Control Plane</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>{APP_VERSION_LABEL}</span>
              </div>
            </div>
            {/* Top-right task indicator (legacy floating ⚡ button) */}
            {hasAny && (
              <button
                onClick={togglePanel}
                title={runningCount > 0 ? `${runningCount} analysis running` : 'Analysis tasks'}
                style={{
                  position: 'fixed',
                  top: 12,
                  right: 60,
                  zIndex: 998,
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(26, 26, 26, 0.9)',
                  backdropFilter: 'blur(8px)',
                  color: '#e0e0e0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                  transition: 'transform 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <Icon name="zap" size={16} />
                {runningCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    background: '#4a8df7',
                    fontSize: 9,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    animation: 'pulse 1.5s infinite',
                  }}>
                    {runningCount}
                  </span>
                )}
              </button>
            )}
            {/* Flag off → the legacy in-flow notice (flag-on path owns this inside
                AppTopbar's sticky second row, so it is NOT rendered in common). */}
            <ReleaseBanner />
          </>
        )}
        {/* Persistent "use the system as role(s)" indicator + reset (admin only). */}
        <ImpersonationBanner />
        {children}
      </main>

      {/* Bottom tab bar — compact screens only (CSS-gated) */}
      <MobileTabBar onMore={() => setMobileOpen(true)} />
    </div>
  );
}
